import { api } from '@/shared/axios-client';
import { useMutation } from '@tanstack/react-query';
import { environment } from '@/environments/environment';

// ── Request / Response types ────────────────────────────────────────────────

export interface StartSessionRequest {
  testerName: string;
  /** Deployed frontend URL of the app under test. */
  targetAppUrl: string;
  scanId?: string;
  repoId?: string;
  branchId?: string;
  /** QTrace platform API URL used for coverage flush (not the repo backend). */
  backendUrl?: string;
  featureName?: string;
  referenceId?: string;
  /** Flow B — one session per test case (ADO / Jira). */
  testCaseId?: string;
  testCaseName?: string;
  suiteId?: number;
  suiteName?: string;
  priority?: string;
  runId?: string;
}

export interface PerTestCaseSessionBinding {
  testCaseId: string;
  testCaseName: string;
  suiteId?: number;
  suiteName?: string;
  priority?: string;
  runId?: string;
}

/** QTrace platform API base URL for manual-testing coverage flush. */
export function getManualTestingPlatformBackendUrl(): string {
  const configured = environment.apiUrl.replace(/\/+$/, '');
  if (configured) return configured;
  // Production often uses relative apiUrl ("/"); use the browser origin.
  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin;
  }
  return 'http://localhost:8080';
}

export function resolveManualTestingTargetAppUrl(
  formFrontendUrl: string | undefined,
  onboardedFrontendUrl: string | undefined,
): string {
  return (onboardedFrontendUrl?.trim() || formFrontendUrl?.trim() || '');
}

export function buildStartManualSessionRequest(params: {
  testerName: string;
  frontendAppUrl: string;
  scanId?: string;
  repoId?: string;
  branchId?: string;
  referenceId?: string;
  featureName?: string;
  testCase?: PerTestCaseSessionBinding;
}): StartSessionRequest {
  const referenceId = params.referenceId?.trim();
  const featureName = params.featureName?.trim();
  const testCase = params.testCase;
  const priority = testCase?.priority?.trim();
  return {
    testerName: params.testerName.trim(),
    targetAppUrl: params.frontendAppUrl.trim(),
    backendUrl: getManualTestingPlatformBackendUrl(),
    scanId: params.scanId || undefined,
    repoId: params.repoId || undefined,
    branchId: params.branchId || undefined,
    ...(referenceId ? { referenceId } : {}),
    ...(featureName ? { featureName } : {}),
    ...(testCase?.testCaseId ? { testCaseId: testCase.testCaseId } : {}),
    ...(testCase?.testCaseName ? { testCaseName: testCase.testCaseName } : {}),
    ...(testCase?.suiteId != null ? { suiteId: testCase.suiteId } : {}),
    ...(testCase?.suiteName ? { suiteName: testCase.suiteName } : {}),
    ...(priority ? { priority } : {}),
    ...(testCase?.runId ? { runId: testCase.runId } : {}),
  };
}

export interface StartSessionResponse {
  sessionId: string;
  testerName: string;
  targetAppUrl: string;
  launchUrl: string;
  status: string;
}

// ── API function ────────────────────────────────────────────────────────────

const startManualSessionFn = (
  params: StartSessionRequest,
): Promise<StartSessionResponse> => {
  return api.post('/api/manual-testing/sessions/start', params);
};

// ── Hook ────────────────────────────────────────────────────────────────────

export const useStartManualSession = () => {
  return useMutation<StartSessionResponse, Error, StartSessionRequest>({
    mutationFn: startManualSessionFn,
  });
};

// ── End Session ─────────────────────────────────────────────────────────────

export interface CoverageSummaryMetric {
  total: number;
  covered: number;
  pct: number;
}

export interface EndSessionCoverageSummary {
  statements: CoverageSummaryMetric;
  functions: CoverageSummaryMetric;
  branches: CoverageSummaryMetric;
  lines: CoverageSummaryMetric;
}

export interface CoverageForMappingEntry {
  filePath: string;
  coveredLines?: number[];
  coverageRanges?: number[][];
}

export interface EndSessionResponse {
  sessionId: string;
  testerName: string;
  duration: string;
  coverageSummary?: EndSessionCoverageSummary;
  filesCovered?: string[];
  totalFiles?: number;
  coverageForMapping?: CoverageForMappingEntry[];
}

export function getEndSessionDisplayStats(data: EndSessionResponse) {
  const fileCount =
    data.filesCovered?.length ??
    data.totalFiles ??
    data.coverageForMapping?.length ??
    0;

  let methodsHit = data.coverageSummary?.functions?.covered;
  if (methodsHit == null && data.coverageForMapping?.length) {
    methodsHit = data.coverageForMapping.reduce(
      (sum, entry) => sum + (entry.coveredLines?.length ?? 0),
      0,
    );
  }

  return {
    methodsHit: methodsHit ?? 0,
    fileCount,
  };
}

const endManualSessionFn = (sessionId: string): Promise<EndSessionResponse> => {
  return api.post(`/api/manual-testing/sessions/${sessionId}/end`);
};

export const useEndManualSession = () => {
  return useMutation<EndSessionResponse, Error, string>({
    mutationFn: endManualSessionFn,
  });
};

// ── Per-TC session result (Flow B) ──────────────────────────────────────────

export type ManualSessionResultStatus = 'passed' | 'failed' | 'skipped';

export interface MarkSessionResultRequest {
  status: ManualSessionResultStatus;
  note?: string;
}

export interface MarkSessionResultResponse {
  sessionId: string;
  testCaseId?: string;
  status: string;
  note?: string;
  markedAt: string;
}

export interface MarkSessionResultMutationArgs {
  sessionId: string;
  body: MarkSessionResultRequest;
}

const markManualSessionResultFn = ({
  sessionId,
  body,
}: MarkSessionResultMutationArgs): Promise<MarkSessionResultResponse> => {
  return api.post(`/api/manual-testing/sessions/${sessionId}/result`, body);
};

export const useMarkManualSessionResult = () => {
  return useMutation<MarkSessionResultResponse, Error, MarkSessionResultMutationArgs>(
    {
      mutationFn: markManualSessionResultFn,
    },
  );
};

// ── Fetch Test Cases from ADO ────────────────────────────────────────────────

export interface FetchTestCasesFromAdoRequest {
  organization: string;
  project: string;
  planId: number;
  suiteIds: number[];
}

export interface AdoTestCase {
  testCaseId: string;
  name: string;
  priority: string;
  state: string;
  automationStatus: string;
}

export interface AdoTestCaseGroup {
  suiteId: number;
  suiteName: string;
  testCases: AdoTestCase[];
}

/** Shared test-case types for manual-testing UIs (ADO, JIRA, GitHub). */
export type ManualTestCase = AdoTestCase;
export type ManualTestCaseGroup = AdoTestCaseGroup;

export interface FetchTestCasesFromAdoResponse {
  planId: number;
  totalTestCases: number;
  groups: AdoTestCaseGroup[];
}

const fetchTestCasesFromAdoFn = (
  body: FetchTestCasesFromAdoRequest,
  patToken: string,
): Promise<FetchTestCasesFromAdoResponse> => {
  return api.post('/api/manual-testing/test-cases/from-ado', body, {
    headers: {
      'Requester-Token': patToken.trim(),
      'Content-Type': 'application/json',
    },
  });
};

export const useFetchTestCasesFromAdo = (patToken: string) => {
  return useMutation<
    FetchTestCasesFromAdoResponse,
    Error,
    FetchTestCasesFromAdoRequest
  >({
    mutationFn: body => fetchTestCasesFromAdoFn(body, patToken),
  });
};

// ── Fetch Test Cases from JIRA ───────────────────────────────────────────────

export interface FetchTestCasesFromJiraRequest {
  organization: string;
  jiraEmail: string;
  jiraApiToken: string;
  project: string;
  pageToken?: string;
}

export interface FetchTestCasesFromJiraResponse {
  totalTestCases: number;
  pageSize: number;
  pageToken: string | null;
  isLast: boolean;
  groups: ManualTestCaseGroup[];
}

const fetchTestCasesFromJiraFn = (
  body: FetchTestCasesFromJiraRequest,
): Promise<FetchTestCasesFromJiraResponse> => {
  return api.post('/api/manual-testing/test-cases/from-jira', body);
};

export const useFetchTestCasesFromJira = () => {
  return useMutation<
    FetchTestCasesFromJiraResponse,
    Error,
    FetchTestCasesFromJiraRequest
  >({
    mutationFn: fetchTestCasesFromJiraFn,
  });
};

// ── Submit Session Test Case Results ────────────────────────────────────────

export type TestCaseStatus = 'passed' | 'failed' | 'skipped';

export interface TestCaseResult {
  testCaseId: string;
  name: string;
  suiteId: number;
  suiteName: string;
  priority: string;
  status: TestCaseStatus;
  note: string | null;
}

export interface SubmitSessionTestCasesRequest {
  taggingState: string;
  results: TestCaseResult[];
}

export interface TaggedTestCaseResult {
  testCaseId: string;
  name: string;
  suiteId: number;
  suiteName: string;
  priority: string;
  status: 'passed' | 'failed' | 'skipped';
  note: string | null;
}

export interface SubmitSessionTestCasesResponse {
  sessionId: string;
  taggingState: string;
  taggedAt: string;
  results: TaggedTestCaseResult[];
}

export interface SubmitSessionTestCasesMutationArgs {
  sessionId: string;
  body: SubmitSessionTestCasesRequest;
}

const submitSessionTestCasesFn = ({
  sessionId,
  body,
}: SubmitSessionTestCasesMutationArgs): Promise<SubmitSessionTestCasesResponse> => {
  return api.post(`/api/manual-testing/sessions/${sessionId}/test-cases`, body);
};

export const useSubmitSessionTestCases = () => {
  return useMutation<
    SubmitSessionTestCasesResponse,
    Error,
    SubmitSessionTestCasesMutationArgs
  >({
    mutationFn: submitSessionTestCasesFn,
  });
};
